import { Component, Input, AfterViewInit, ElementRef } from '@angular/core';
import { AVAILABLEDOCUMENTTYPES, PAYMENTMETHODS } from '../../../document/document.data';

@Component({
	selector: 'app-invoice-template3',
	templateUrl: './invoice-template3.component.html',
	styleUrls: ['./invoice-template3.component.scss']
})
export class InvoiceTemplate3Component implements AfterViewInit {
	@Input() invoiceData: any;
	@Input() lang: string = 'he';

	constructor(private elRef: ElementRef) {
		}

	ngAfterViewInit(): void {
		this.logDimensions();
		setTimeout(() => {
			this.logDimensions();
		}, 1000);
	}

	private logDimensions(): void {
		const hostElement = this.elRef.nativeElement as HTMLElement;
		const invoiceElement = hostElement.querySelector('.invoice') as HTMLElement;
		
}

	get direction(): string {
		return this.lang === 'ar' || this.lang === 'he' ? 'rtl' : 'ltr';
	}

	getCompanyName(): string {
		if (this.lang === 'en') {
			return this.invoiceData?.company?.english_name || this.invoiceData?.company?.name || '';
		} else if (this.lang === 'ar') {
			return this.invoiceData?.company?.arabic_name || this.invoiceData?.company?.name || '';
		}
		return this.invoiceData?.company?.name || '';
	}

	getCompanyVat(): string {
		return this.invoiceData?.company?.vat || '';
	}

	getCompanyAddress(): string {
		return this.invoiceData?.company?.address || '';
	}

	getCompanyPhone(): string {
		return this.invoiceData?.company?.phone || '';
	}

	getCustomerName(): string {
		if (this.lang === 'en') {
			return this.invoiceData?.customer?.english_name || this.invoiceData?.customer?.name || '';
		} else if (this.lang === 'ar') {
			return this.invoiceData?.customer?.arabic_name || this.invoiceData?.customer?.name || '';
		}
		return this.invoiceData?.customer?.name || '';
	}

	getCustomerEmail(): string {
		return this.invoiceData?.customer?.email || '';
	}

	getCustomerVat(): string {
		return this.invoiceData?.customer?.vat || '';
	}

	getInvoiceTypeName(): string {
		const invoiceType = this.invoiceData?.invoice_type;
		if (!invoiceType) return '';

		switch (this.lang) {
			case 'en':
				return invoiceType.english_name || invoiceType.name || '';
			case 'ar':
				return invoiceType.arabic_name || invoiceType.name || '';
			default:
				return invoiceType.name || '';
		}
	}

	getFormattedDate(): string {
		if (!this.invoiceData?.date) return '';
		
		const date = new Date(this.invoiceData.date);
		return date.toLocaleDateString(this.lang === 'en' ? 'en-US' : this.lang === 'ar' ? 'ar-EG' : 'he-IL');
	}

	getConfirmationNumber(): { remainingDigits: string; last9Digits: string } {
		const confirmationNumber = this.invoiceData?.confirmation_number || '';
		if (confirmationNumber.length <= 9) {
			return { remainingDigits: '', last9Digits: confirmationNumber };
		}
		return {
			remainingDigits: confirmationNumber.slice(0, -9),
			last9Digits: confirmationNumber.slice(-9)
		};
	}

	formatNumber(value: any): string {
		if (value === null || value === undefined || value === '') return '0.00';
		
		const num = typeof value === 'string' ? parseFloat(value) : value;
		if (isNaN(num)) return '0.00';
		
		return num.toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		});
	}

	getPaymentMethodName(paymentType: any): string {
		if (!paymentType) return '';
		
		const typeNum = typeof paymentType === 'object' ? paymentType.payment_type : paymentType;
		
		switch (typeNum) {
			case 1:
				switch (this.lang) {
					case 'en': return 'Cash';
					case 'ar': return 'نقدي';
					default: return 'מזומן';
				}
			case 2:
				switch (this.lang) {
					case 'en': return 'Check';
					case 'ar': return 'شيك';
					default: return 'שיק';
				}
			case 3:
				switch (this.lang) {
					case 'en': return 'Credit';
					case 'ar': return 'بطاقة ائتمان';
					default: return 'כרטיס אשראי';
				}
			case 4:
				switch (this.lang) {
					case 'en': return 'Bank Transfer';
					case 'ar': return 'حوالة بنكية';
					default: return 'העברה בנקאית';
				}
			default:
				if (typeof paymentType === 'object') {
					switch (this.lang) {
						case 'en':
							return paymentType.english_name || paymentType.name || '';
						case 'ar':
							return paymentType.arabic_name || paymentType.name || '';
						default:
							return paymentType.name || '';
					}
				}
				return '';
		}
	}

	/**
	 * Check if the current document is a receipt
	 * Receipt types: 400 (Receipt) and 430 (Cancelled Receipt)
	 */
	isReceipt(): boolean {
		const invoiceType = String(this.invoiceData?.invoice_type || '');
		return invoiceType === '400' || invoiceType === '430';
	}

	getPaymentAmountIncludingVat(): number {
		// For INVOICES: Use the standard calculation (payment_amount + VAT)
		// Do NOT use receiptsDetails for invoice totals - they are for payment allocation only
		if (!this.isReceipt()) {
			const providedValue = parseFloat(this.invoiceData?.payment_amount_including_vat);
			if (!isNaN(providedValue) && providedValue > 0) {
				return providedValue;
			}
			
			const paymentAmount = parseFloat(this.invoiceData?.payment_amount) || 0;
			const vatAmount = parseFloat(this.invoiceData?.all_vat_amount) || 0;
			return paymentAmount + vatAmount;
		}

		// For RECEIPTS: Sum up all payment amounts from receiptsDetails (excluding deductions)
		if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails) && this.invoiceData.receiptsDetails.length > 0) {
			const totalFromReceipts = this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
				const paymentType = Number(receipt.payment_type) || 0;
				const amount = parseFloat(receipt.amount) || 0;
				// Include all payment types except deductions (type 5)
				if (paymentType !== 5) {
					return sum + amount;
				}
				return sum;
			}, 0);
			
			// For receipts, ADD deduction instead of subtracting (deduction is additional amount)
			const deduction = this.getDeductionAmount();
			// For receipts: Total = Payments + Deduction
			const finalTotal = totalFromReceipts + deduction;
			if (finalTotal > 0) {
				return finalTotal;
			}
			// If deduction makes total negative or zero, return the payments total before deduction
			if (totalFromReceipts > 0) {
				return totalFromReceipts;
			}
		}

		// Fallback for receipts
		const providedValue = parseFloat(this.invoiceData?.payment_amount_including_vat);
		if (!isNaN(providedValue) && providedValue > 0) {
			return providedValue;
		}
		
		const paymentAmount = parseFloat(this.invoiceData?.payment_amount) || 0;
		const vatAmount = parseFloat(this.invoiceData?.all_vat_amount) || 0;
		return paymentAmount + vatAmount;
	}

	/**
	 * Get deduction amount from receiptsDetails or deduction_from_source
	 */
	getDeductionAmount(): number {
		// First check if deduction is in receiptsDetails (payment_type 5)
		if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails)) {
			const deductionFromReceipts = this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
				if (Number(receipt.payment_type) === 5) {
					return sum + (parseFloat(receipt.amount) || 0);
				}
				return sum;
			}, 0);
			
			if (deductionFromReceipts > 0) {
				return deductionFromReceipts;
			}
		}
		
		// Fallback to deduction_from_source field
		const deductionFromSource = parseFloat(this.invoiceData?.deduction_from_source || '0');
		return deductionFromSource || 0;
	}

	getBankName(bank: any): string {
		if (!bank) return '';
		if (typeof bank === 'string') return bank;
		if (typeof bank === 'object') {
			return bank.bank_name || bank.name || bank.bank_name_en || bank.bank_name_he || bank.bank_name_ar || '';
		}
		return '';
	}

	getVatPercentage(): number {
		const vatPercentage = this.invoiceData?.vat_percentage || 
							  this.invoiceData?.vat_rate || 
							  this.invoiceData?.vatPercentage ||
							  this.invoiceData?.lines?.[0]?.vat_rate ||
							  this.invoiceData?.soldItems?.[0]?.vat_rate ||
							  0;
		return parseFloat(vatPercentage) || 0;
	}
}
